import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface CTORecruitmentProps {
    onBack: () => void;
}

interface CTOCandidate {
    name: string;
    currentRole: string;
    company: string;
    experience: string;
    techStack: string[];
    achievements: string[];
    education: string;
    location: string;
    availability: string;
    expectedSalary: string;
    status: 'Target' | 'Initial Contact' | 'Phone Screen' | 'Technical Interview' | 'Final Interview' | 'Offer Extended' | 'Negotiating' | 'Hired';
    notes: string;
    linkedinProfile?: string;
    githubProfile?: string;
    portfolio?: string;
    interviewNotes?: string[];
}

const CTORecruitment: React.FC<CTORecruitmentProps> = ({ onBack }) => {
    const [activeTab, setActiveTab] = useState<'candidates' | 'requirements' | 'process' | 'offers'>('candidates');

    const ctoJobRequirements = {
        title: "Chief Technology Officer (CTO)",
        department: "Executive Leadership",
        location: "New York, NY (Hybrid)",
        salaryRange: "$180K - $250K + Equity",
        experienceLevel: "10+ years in technology leadership",
        keyResponsibilities: [
            "Lead technical strategy and architecture for NYC Energy Retrofit platform",
            "Scale engineering team from 2 to 15+ developers over 18 months",
            "Drive product development roadmap aligned with business objectives",
            "Ensure platform security, scalability, and compliance with NYC regulations",
            "Partner with CEO on fundraising and investor technical presentations",
            "Build strategic technology partnerships and integration capabilities",
            "Oversee data analytics, AI/ML initiatives, and automation systems"
        ],
        requiredSkills: [
            "Full-stack development expertise (React, Node.js, TypeScript)",
            "Cloud architecture experience (AWS, Azure, or GCP)",
            "Database design and optimization (PostgreSQL, MongoDB)",
            "API development and microservices architecture",
            "Data analytics and business intelligence platforms",
            "Team leadership and technical mentoring experience",
            "Startup scaling experience with remote/hybrid teams"
        ],
        preferredQualifications: [
            "PropTech, FinTech, or GovTech industry experience",
            "Experience with energy data, IoT, or building management systems",
            "Previous CTO role at growth-stage startup",
            "NYC real estate or construction technology background",
            "Open source contributions and technical thought leadership",
            "MBA or advanced technical degree",
            "Previous successful exit or IPO experience"
        ]
    };

    const ctoCandidates: CTOCandidate[] = [
        {
            name: "Alex Chen",
            currentRole: "VP of Engineering",
            company: "BuildTech Solutions",
            experience: "12 years",
            techStack: ["React", "Node.js", "AWS", "PostgreSQL", "Python", "Docker"],
            achievements: [
                "Scaled engineering team from 5 to 40 developers",
                "Led $50M Series B fundraising technical presentations",
                "Built multi-tenant SaaS platform serving 500+ property management companies",
                "Reduced infrastructure costs by 40% through cloud optimization"
            ],
            education: "MS Computer Science, Stanford University",
            location: "New York, NY",
            availability: "3-4 weeks notice",
            expectedSalary: "$220K + Equity",
            status: "Technical Interview",
            notes: "Strong PropTech background with proven scaling experience. Excellent cultural fit and technical depth.",
            linkedinProfile: "linkedin.com/in/alexchen-tech",
            githubProfile: "github.com/alexchen",
            portfolio: "alexchen.dev",
            interviewNotes: [
                "Phone Screen (9/10): Excellent communication, strong vision for platform scaling",
                "Technical Interview: Deep knowledge of React/Node.js, good architectural thinking"
            ]
        },
        {
            name: "Sarah Rodriguez",
            currentRole: "CTO",
            company: "GreenTech Innovations",
            experience: "15 years",
            techStack: ["TypeScript", "React", "AWS", "GraphQL", "MongoDB", "Kubernetes"],
            achievements: [
                "Led successful IPO as CTO (company valued at $2B)",
                "Built platform processing $100M+ in green energy transactions",
                "Managed 60+ person engineering organization",
                "Led technical due diligence for 3 major acquisitions"
            ],
            education: "PhD Computer Science, MIT",
            location: "San Francisco, CA (Open to NYC)",
            availability: "6-8 weeks notice",
            expectedSalary: "$250K + Significant Equity",
            status: "Final Interview",
            notes: "Outstanding credentials and proven track record. Concerned about salary requirements but strong interest in NYC energy sector.",
            linkedinProfile: "linkedin.com/in/sarah-rodriguez-cto",
            githubProfile: "github.com/srodriguez",
            portfolio: "sarahrodriguez.tech",
            interviewNotes: [
                "Phone Screen (10/10): Exceptional vision and leadership experience",
                "Technical Interview (9/10): Deep architectural knowledge, excellent problem-solving",
                "CEO Interview (9/10): Strong strategic thinking and business acumen"
            ]
        },
        {
            name: "Michael Kim",
            currentRole: "Senior Engineering Director",
            company: "Sidewalk Labs (Google)",
            experience: "11 years",
            techStack: ["Python", "React", "GCP", "BigQuery", "TensorFlow", "Kubernetes"],
            achievements: [
                "Led smart city initiatives with $200M+ in municipal contracts",
                "Built IoT platform managing 10,000+ building sensors",
                "Published 15+ patents in urban technology and energy efficiency",
                "Managed cross-functional teams of 25+ engineers and data scientists"
            ],
            education: "MS Data Science, Carnegie Mellon University",
            location: "New York, NY",
            availability: "4-6 weeks notice",
            expectedSalary: "$200K + Equity",
            status: "Phone Screen",
            notes: "Perfect domain expertise in smart buildings and urban tech. Strong Google pedigree. Scheduling technical interview.",
            linkedinProfile: "linkedin.com/in/michael-kim-urbantech",
            githubProfile: "github.com/mkim-urbantech",
            interviewNotes: [
                "Initial Contact: Very interested, strong alignment with mission"
            ]
        },
        {
            name: "Jennifer Park",
            currentRole: "Head of Engineering",
            company: "Compass Real Estate",
            experience: "9 years",
            techStack: ["TypeScript", "React", "Node.js", "AWS", "PostgreSQL", "Redis"],
            achievements: [
                "Scaled Compass platform to handle $100B+ in real estate transactions",
                "Led team of 35+ engineers across multiple product lines",
                "Built real-time property valuation algorithms",
                "Reduced API response times by 60% through optimization"
            ],
            education: "BS Computer Science, UC Berkeley",
            location: "New York, NY",
            availability: "2-3 weeks notice",
            expectedSalary: "$190K + Equity",
            status: "Initial Contact",
            notes: "Strong real estate tech experience but may lack broader startup scaling experience. Good cultural fit potential.",
            linkedinProfile: "linkedin.com/in/jennifer-park-engineering",
            githubProfile: "github.com/jpark-compass",
            interviewNotes: []
        },
        {
            name: "David Thompson",
            currentRole: "CTO",
            company: "EnergyHub (Startup)",
            experience: "14 years",
            techStack: ["Java", "React", "Azure", "SQL Server", "Spark", "Docker"],
            achievements: [
                "Raised $25M Series A with strong technical pitch",
                "Built energy management platform for 200+ utility companies",
                "Led technical team through successful acquisition by Honeywell",
                "Expertise in energy data analytics and grid optimization"
            ],
            education: "MS Electrical Engineering, Georgia Tech",
            location: "Atlanta, GA (Open to relocation)",
            availability: "Immediate",
            expectedSalary: "$185K + Equity",
            status: "Target",
            notes: "Perfect energy sector experience but needs outreach. Previous successful exit is very attractive.",
            linkedinProfile: "linkedin.com/in/david-thompson-energytech",
            githubProfile: "github.com/dthompson-energy",
            interviewNotes: []
        }
    ];

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Target': return 'bg-gray-100 text-gray-800';
            case 'Initial Contact': return 'bg-blue-100 text-blue-800';
            case 'Phone Screen': return 'bg-purple-100 text-purple-800';
            case 'Technical Interview': return 'bg-yellow-100 text-yellow-800';
            case 'Final Interview': return 'bg-orange-100 text-orange-800';
            case 'Offer Extended': return 'bg-green-100 text-green-800';
            case 'Negotiating': return 'bg-red-100 text-red-800';
            case 'Hired': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">CTO Recruitment</h1>
                    <p className="text-xl text-gray-600 mb-4">
                        Find and hire a world-class Chief Technology Officer to scale our platform
                    </p>
                    
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <p className="text-purple-800 text-sm">
                            <strong>🎯 Hiring Priority:</strong> Critical leadership hire to scale engineering team from 2 to 15+ developers. 
                            Target candidate with PropTech/EnergyTech experience and proven startup scaling track record.
                        </p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-xl shadow-lg mb-8">
                    <div className="border-b border-gray-200">
                        <nav className="flex">
                            <button
                                onClick={() => setActiveTab('candidates')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'candidates'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                👥 Candidates
                            </button>
                            <button
                                onClick={() => setActiveTab('requirements')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'requirements'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                📋 Job Requirements
                            </button>
                            <button
                                onClick={() => setActiveTab('process')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'process'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                🔄 Interview Process
                            </button>
                            <button
                                onClick={() => setActiveTab('offers')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'offers'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                💼 Compensation
                            </button>
                        </nav>
                    </div>

                    <div className="p-8">
                        {/* Candidates Tab */}
                        {activeTab === 'candidates' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-semibold text-gray-900">CTO Candidate Pipeline</h2>
                                    <div className="text-right">
                                        <div className="text-sm text-gray-600">Active Candidates</div>
                                        <div className="text-2xl font-bold text-blue-600">{ctoCandidates.length}</div>
                                        <div className="text-xs text-gray-500 mt-1">In process</div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    {ctoCandidates.map((candidate, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="text-xl font-bold text-gray-900">{candidate.name}</h3>
                                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(candidate.status)}`}>
                                                            {candidate.status}
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Current Role:</span>
                                                            <p className="text-gray-900 font-medium">{candidate.currentRole}</p>
                                                            <p className="text-gray-600 text-sm">{candidate.company}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Experience:</span>
                                                            <p className="text-gray-900 font-medium">{candidate.experience}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Location:</span>
                                                            <p className="text-gray-900">{candidate.location}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Expected Salary:</span>
                                                            <p className="text-green-600 font-semibold">{candidate.expectedSalary}</p>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="mb-4">
                                                        <span className="text-sm font-medium text-gray-600">Technical Stack:</span>
                                                        <div className="flex flex-wrap gap-2 mt-1">
                                                            {candidate.techStack.map((tech, idx) => (
                                                                <span key={idx} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                                                    {tech}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="mb-4">
                                                        <span className="text-sm font-medium text-gray-600">Key Achievements:</span>
                                                        <ul className="text-sm text-gray-700 space-y-1 mt-1">
                                                            {candidate.achievements.map((achievement, idx) => (
                                                                <li key={idx} className="flex items-start">
                                                                    <span className="text-green-500 mr-2">•</span>
                                                                    {achievement}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>

                                                    {candidate.interviewNotes && candidate.interviewNotes.length > 0 && (
                                                        <div className="mb-4">
                                                            <span className="text-sm font-medium text-gray-600">Interview Progress:</span>
                                                            <div className="bg-white rounded-lg p-3 mt-1">
                                                                {candidate.interviewNotes.map((note, idx) => (
                                                                    <p key={idx} className="text-sm text-gray-700 mb-1">{note}</p>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="bg-gray-100 rounded-lg p-3 mb-4">
                                                        <p className="text-sm text-gray-700">{candidate.notes}</p>
                                                    </div>

                                                    <div className="flex space-x-2 mb-4">
                                                        {candidate.linkedinProfile && (
                                                            <a 
                                                                href={`https://${candidate.linkedinProfile}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors"
                                                            >
                                                                LinkedIn
                                                            </a>
                                                        )}
                                                        {candidate.githubProfile && (
                                                            <a 
                                                                href={`https://${candidate.githubProfile}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="bg-gray-800 hover:bg-gray-900 text-white py-2 px-4 rounded text-sm transition-colors"
                                                            >
                                                                GitHub
                                                            </a>
                                                        )}
                                                        {candidate.portfolio && (
                                                            <a 
                                                                href={`https://${candidate.portfolio}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded text-sm transition-colors"
                                                            >
                                                                Portfolio
                                                            </a>
                                                        )}
                                                    </div>

                                                    <div className="flex space-x-2">
                                                        <button className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                            Schedule Interview
                                                        </button>
                                                        <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                            Send Message
                                                        </button>
                                                        <button className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                            Update Status
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Job Requirements Tab */}
                        {activeTab === 'requirements' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">CTO Position Details</h2>
                                
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    <div className="space-y-6">
                                        <div className="bg-white rounded-lg p-6 border border-gray-200">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Position Overview</h3>
                                            <div className="space-y-3">
                                                <div>
                                                    <span className="text-sm font-medium text-gray-600">Title:</span>
                                                    <p className="text-gray-900">{ctoJobRequirements.title}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-600">Department:</span>
                                                    <p className="text-gray-900">{ctoJobRequirements.department}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-600">Location:</span>
                                                    <p className="text-gray-900">{ctoJobRequirements.location}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-600">Salary Range:</span>
                                                    <p className="text-green-600 font-semibold">{ctoJobRequirements.salaryRange}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-600">Experience Level:</span>
                                                    <p className="text-gray-900">{ctoJobRequirements.experienceLevel}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white rounded-lg p-6 border border-gray-200">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Required Technical Skills</h3>
                                            <ul className="space-y-2">
                                                {ctoJobRequirements.requiredSkills.map((skill, index) => (
                                                    <li key={index} className="flex items-start">
                                                        <span className="text-blue-500 mr-2">•</span>
                                                        <span className="text-sm text-gray-700">{skill}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="bg-white rounded-lg p-6 border border-gray-200">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Responsibilities</h3>
                                            <ul className="space-y-2">
                                                {ctoJobRequirements.keyResponsibilities.map((responsibility, index) => (
                                                    <li key={index} className="flex items-start">
                                                        <span className="text-green-500 mr-2">•</span>
                                                        <span className="text-sm text-gray-700">{responsibility}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="bg-white rounded-lg p-6 border border-gray-200">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Preferred Qualifications</h3>
                                            <ul className="space-y-2">
                                                {ctoJobRequirements.preferredQualifications.map((qualification, index) => (
                                                    <li key={index} className="flex items-start">
                                                        <span className="text-purple-500 mr-2">⭐</span>
                                                        <span className="text-sm text-gray-700">{qualification}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Interview Process Tab */}
                        {activeTab === 'process' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">CTO Interview Process</h2>
                                
                                <div className="space-y-8">
                                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Interview Timeline</h3>
                                        <div className="space-y-6">
                                            <div className="flex items-start">
                                                <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">1</div>
                                                <div>
                                                    <h4 className="text-lg font-semibold mb-2">Initial Screen (30 min)</h4>
                                                    <p className="text-gray-700 mb-2">Recruiter/CEO phone call to assess basic fit, experience, and interest level.</p>
                                                    <ul className="text-sm text-gray-600 space-y-1">
                                                        <li>• Background and experience review</li>
                                                        <li>• Interest in PropTech/EnergyTech sector</li>
                                                        <li>• Salary expectations and timeline</li>
                                                        <li>• High-level culture and mission alignment</li>
                                                    </ul>
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-start">
                                                <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">2</div>
                                                <div>
                                                    <h4 className="text-lg font-semibold mb-2">Technical Interview (90 min)</h4>
                                                    <p className="text-gray-700 mb-2">Deep dive into technical architecture, system design, and hands-on experience.</p>
                                                    <ul className="text-sm text-gray-600 space-y-1">
                                                        <li>• System architecture design exercise</li>
                                                        <li>• Code review and technical problem solving</li>
                                                        <li>• Scaling strategies and performance optimization</li>
                                                        <li>• Security and compliance considerations</li>
                                                    </ul>
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-start">
                                                <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">3</div>
                                                <div>
                                                    <h4 className="text-lg font-semibold mb-2">Leadership & Strategy (60 min)</h4>
                                                    <p className="text-gray-700 mb-2">CEO interview focused on leadership philosophy, strategic thinking, and vision.</p>
                                                    <ul className="text-sm text-gray-600 space-y-1">
                                                        <li>• Team building and scaling strategies</li>
                                                        <li>• Product roadmap and technical strategy</li>
                                                        <li>• Fundraising and investor relations experience</li>
                                                        <li>• Conflict resolution and decision-making style</li>
                                                    </ul>
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-start">
                                                <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">4</div>
                                                <div>
                                                    <h4 className="text-lg font-semibold mb-2">Team Fit Interview (45 min)</h4>
                                                    <p className="text-gray-700 mb-2">Meet with current engineering team and key stakeholders.</p>
                                                    <ul className="text-sm text-gray-600 space-y-1">
                                                        <li>• Current team member interviews</li>
                                                        <li>• Collaborative problem-solving exercise</li>
                                                        <li>• Communication style assessment</li>
                                                        <li>• Technical mentoring approach</li>
                                                    </ul>
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-start">
                                                <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">5</div>
                                                <div>
                                                    <h4 className="text-lg font-semibold mb-2">Reference Check & Offer</h4>
                                                    <p className="text-gray-700 mb-2">Final reference verification and compensation negotiation.</p>
                                                    <ul className="text-sm text-gray-600 space-y-1">
                                                        <li>• Professional reference calls</li>
                                                        <li>• Background and education verification</li>
                                                        <li>• Equity and compensation discussion</li>
                                                        <li>• Start date and transition planning</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-green-50 rounded-lg p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Interview Panel</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <h4 className="font-medium text-gray-900 mb-2">Primary Interviewers</h4>
                                                <ul className="text-sm text-gray-700 space-y-1">
                                                    <li>• CEO (Strategy & Vision)</li>
                                                    <li>• Current Lead Developer (Technical)</li>
                                                    <li>• Board Member/Advisor (Leadership)</li>
                                                    <li>• External Technical Consultant</li>
                                                </ul>
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-gray-900 mb-2">Interview Criteria</h4>
                                                <ul className="text-sm text-gray-700 space-y-1">
                                                    <li>• Technical depth and breadth (30%)</li>
                                                    <li>• Leadership and team building (25%)</li>
                                                    <li>• Strategic thinking (20%)</li>
                                                    <li>• Cultural fit and communication (15%)</li>
                                                    <li>• Industry experience (10%)</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Compensation Tab */}
                        {activeTab === 'offers' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">CTO Compensation Package</h2>
                                
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    <div className="space-y-6">
                                        <div className="bg-white rounded-lg p-6 border border-gray-200">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Base Compensation</h3>
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="text-gray-600">Salary Range:</span>
                                                    <span className="font-semibold text-green-600">$180K - $250K</span>
                                                </div>
                                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="text-gray-600">Target Salary:</span>
                                                    <span className="font-semibold">$220K</span>
                                                </div>
                                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="text-gray-600">Annual Bonus:</span>
                                                    <span className="font-semibold">10-20% of base</span>
                                                </div>
                                                <div className="flex justify-between items-center py-2">
                                                    <span className="text-gray-600">Performance Review:</span>
                                                    <span className="font-semibold">Every 6 months</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white rounded-lg p-6 border border-gray-200">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Equity Package</h3>
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="text-gray-600">Equity Range:</span>
                                                    <span className="font-semibold text-purple-600">2% - 5%</span>
                                                </div>
                                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="text-gray-600">Vesting Schedule:</span>
                                                    <span className="font-semibold">4 years, 1-year cliff</span>
                                                </div>
                                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="text-gray-600">Strike Price:</span>
                                                    <span className="font-semibold">$0.10 per share</span>
                                                </div>
                                                <div className="flex justify-between items-center py-2">
                                                    <span className="text-gray-600">Double Trigger:</span>
                                                    <span className="font-semibold">Yes (acquisition)</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="bg-white rounded-lg p-6 border border-gray-200">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Benefits Package</h3>
                                            <ul className="space-y-3">
                                                <li className="flex items-start">
                                                    <span className="text-green-500 mr-2">✓</span>
                                                    <div>
                                                        <span className="font-medium">Health Insurance</span>
                                                        <p className="text-sm text-gray-600">100% premium coverage for employee, 80% for family</p>
                                                    </div>
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-green-500 mr-2">✓</span>
                                                    <div>
                                                        <span className="font-medium">Flexible PTO</span>
                                                        <p className="text-sm text-gray-600">Unlimited vacation policy with 3-week minimum</p>
                                                    </div>
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-green-500 mr-2">✓</span>
                                                    <div>
                                                        <span className="font-medium">Remote Work</span>
                                                        <p className="text-sm text-gray-600">Hybrid model with flexible home office setup</p>
                                                    </div>
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-green-500 mr-2">✓</span>
                                                    <div>
                                                        <span className="font-medium">Learning & Development</span>
                                                        <p className="text-sm text-gray-600">$5K annual professional development budget</p>
                                                    </div>
                                                </li>
                                                <li className="flex items-start">
                                                    <span className="text-green-500 mr-2">✓</span>
                                                    <div>
                                                        <span className="font-medium">Equipment Allowance</span>
                                                        <p className="text-sm text-gray-600">$3K laptop/equipment budget + annual refresh</p>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-blue-50 rounded-lg p-6">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Total Compensation Example</h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Base Salary:</span>
                                                    <span className="font-semibold">$220,000</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Target Bonus (15%):</span>
                                                    <span className="font-semibold">$33,000</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Equity Value (3% @ $10M):</span>
                                                    <span className="font-semibold">$300,000</span>
                                                </div>
                                                <div className="flex justify-between border-t border-gray-300 pt-2">
                                                    <span className="font-semibold">Total Package Value:</span>
                                                    <span className="font-bold text-green-600">$553,000</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 bg-yellow-50 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Competitive Analysis</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="text-center">
                                            <h4 className="font-medium text-gray-900 mb-2">Early Stage Startups</h4>
                                            <p className="text-2xl font-bold text-blue-600">$160K-$200K</p>
                                            <p className="text-sm text-gray-600">+ 3-8% equity</p>
                                        </div>
                                        <div className="text-center">
                                            <h4 className="font-medium text-gray-900 mb-2">Our Offer</h4>
                                            <p className="text-2xl font-bold text-green-600">$180K-$250K</p>
                                            <p className="text-sm text-gray-600">+ 2-5% equity</p>
                                        </div>
                                        <div className="text-center">
                                            <h4 className="font-medium text-gray-900 mb-2">Series A Companies</h4>
                                            <p className="text-2xl font-bold text-purple-600">$220K-$280K</p>
                                            <p className="text-sm text-gray-600">+ 1-3% equity</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Link
                        to="/staff-hiring"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">👥</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Staff Hiring</h3>
                            <p className="text-sm text-gray-600">Additional team member recruitment</p>
                        </div>
                    </Link>
                    
                    <Link
                        to="/contractor-partnerships"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">🔧</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Contractor Partnerships</h3>
                            <p className="text-sm text-gray-600">Service provider network</p>
                        </div>
                    </Link>
                    
                    <Link
                        to="/company-management"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">📊</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Company Management</h3>
                            <p className="text-sm text-gray-600">Business operations and growth</p>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default CTORecruitment;
